/**
 * Delivery Type Detection Utility
 *
 * Determines the type of delivery for a location based on its configuration.
 * Supports three delivery models:
 * 1. No delivery (pickup only)
 * 2. Area-based delivery (existing model with predefined delivery areas)
 * 3. Distance/time-based delivery (new model using Google Maps API)
 */

/**
 * Delivery type constants
 */
export const DELIVERY_TYPES = {
  NONE: 'none',
  DELIVERY_AREAS: 'delivery_areas',
  DELIVERY_DISTANCE: 'delivery_distance',
};

/**
 * Get the delivery type for a location
 *
 * @param {Object} location - Location object from API or merchant data
 * @returns {string} - One of: 'none', 'delivery_areas', 'delivery_distance'
 */
export function getDeliveryType(location) {
  if (!location) {
    return DELIVERY_TYPES.NONE;
  }

  // Extract deliveries flag from either _apiData or direct property
  const deliveries = location._apiData?.deliveries ?? location.deliveries ?? false;

  // Not a delivery location at all
  if (!deliveries) {
    return DELIVERY_TYPES.NONE;
  }

  // Check if distance/time-based pricing is configured
  const pricingType = location._apiData?.delivery_pricing_type
    ?? location.delivery_pricing_type
    ?? null;

  // Has distance/time pricing config → distance-based delivery (new model)
  if (pricingType) {
    return DELIVERY_TYPES.DELIVERY_DISTANCE;
  }

  // Has delivery enabled but no distance pricing → area-based delivery (existing model)
  return DELIVERY_TYPES.DELIVERY_AREAS;
}

/**
 * Check if a location supports any delivery
 *
 * @param {Object} location - Location object
 * @returns {boolean} - True if location supports delivery
 */
export function isDeliveryLocation(location) {
  const deliveryType = getDeliveryType(location);
  return deliveryType !== DELIVERY_TYPES.NONE;
}

/**
 * Check if a location uses area-based delivery
 *
 * @param {Object} location - Location object
 * @returns {boolean} - True if location uses delivery areas
 */
export function isDeliveryAreas(location) {
  return getDeliveryType(location) === DELIVERY_TYPES.DELIVERY_AREAS;
}

/**
 * Check if a location uses distance/time-based delivery
 *
 * @param {Object} location - Location object
 * @returns {boolean} - True if location uses distance/time pricing
 */
export function isDeliveryDistance(location) {
  return getDeliveryType(location) === DELIVERY_TYPES.DELIVERY_DISTANCE;
}

/**
 * Get delivery configuration for distance/time-based delivery
 *
 * @param {Object} location - Location object
 * @returns {Object|null} - Delivery config or null if not distance-based
 */
export function getDeliveryConfig(location) {
  if (!isDeliveryDistance(location)) {
    return null;
  }

  const apiData = location._apiData || location;

  return {
    pricingType: apiData.delivery_pricing_type,
    distanceUnit: apiData.delivery_distance_unit,
    pricingModel: apiData.delivery_pricing_model,
    pricePerUnit: apiData.delivery_price_per_unit,
    priceBrackets: apiData.delivery_price_brackets,
    maxServiceArea: apiData.delivery_max_service_area,
    originAddress: apiData.pickup_address,
  };
}
